// İSG uzmanı portalı — Ziyaret detayı (Modal, salt-okunur). api-contract §11 `VisitDto`.
// Detay, listeden gelen satır verisinden render edilir (ek istek yok) — bu hem gereksiz
// PII cache'lemeyi önler ([[CLAUDE.md]] KVKK) hem de gereksiz `GET /visits/{uuid}`
// çağrısını eler; `VisitDto` (liste + get) e-posta/kimlik no TAŞIMAZ (yalnız `VisitorDto`
// taşır) — böylece hassas alanlar listede/detayda gösterilmez. Zamanlar kullanıcı-yerel.

import { FormSection, Modal, Tag, type VisitDto, currentCulture } from '@isgportal/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../lib/format';

interface VisitDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visit: VisitDto | null;
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1.5">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className="col-span-2 text-sm text-fg">{children}</dd>
    </div>
  );
}

export function VisitDetailModal({ open, onOpenChange, visit }: VisitDetailModalProps) {
  const { t } = useTranslation();
  const culture = currentCulture();

  if (!visit) {
    return null;
  }

  const location = visit.siteName ?? visit.branchName ?? '—';
  const statusLabel =
    visit.status === 'OnSite' ? t('visitors.status.onSite') : t('visitors.status.checkedOut');
  const entryLabel =
    visit.entryMethod === 'Kiosk'
      ? t('visitors.entryMethod.kiosk')
      : t('visitors.entryMethod.manual');

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('visitors.detail.title')}
      description={`${visit.visitor.firstName} ${visit.visitor.lastName}`.trim()}
      closeLabel={t('visitors.common.close')}
    >
      <div className="flex flex-col gap-8">
        <FormSection legend={t('visitors.detail.section.visitor')} variant="section">
          <dl className="divide-y divide-border">
            <DetailRow label={t('visitors.detail.field.phone')}>
              {visit.visitor.phone ?? '—'}
            </DetailRow>
            <DetailRow label={t('visitors.detail.field.organization')}>
              {visit.visitor.organization ?? '—'}
            </DetailRow>
          </dl>
        </FormSection>

        <FormSection legend={t('visitors.detail.section.visit')} variant="section">
          <dl className="divide-y divide-border">
            <DetailRow label={t('visitors.detail.field.status')}>
              <Tag color={visit.status === 'OnSite' ? 'green' : 'gray'}>{statusLabel}</Tag>
            </DetailRow>
            <DetailRow label={t('visitors.detail.field.location')}>{location}</DetailRow>
            <DetailRow label={t('visitors.detail.field.host')}>{visit.hostName ?? '—'}</DetailRow>
            <DetailRow label={t('visitors.detail.field.purpose')}>{visit.purpose ?? '—'}</DetailRow>
            <DetailRow label={t('visitors.detail.field.entryMethod')}>{entryLabel}</DetailRow>
            <DetailRow label={t('visitors.detail.field.checkInAt')}>
              {formatDateTime(visit.checkInAt, culture)}
            </DetailRow>
            <DetailRow label={t('visitors.detail.field.checkOutAt')}>
              {formatDateTime(visit.checkOutAt, culture)}
            </DetailRow>
            <DetailRow label={t('visitors.detail.field.briefing')}>
              {visit.safetyBriefingAcceptedAt
                ? `${t('visitors.detail.briefingAccepted')} · ${formatDateTime(visit.safetyBriefingAcceptedAt, culture)}`
                : t('visitors.detail.briefingNotAccepted')}
            </DetailRow>
            {visit.badgeCode ? (
              <DetailRow label={t('visitors.detail.field.badge')}>
                <code className="rounded bg-surface-muted px-1.5 py-0.5 text-xs">
                  {visit.badgeCode}
                </code>
              </DetailRow>
            ) : null}
          </dl>
        </FormSection>
      </div>
    </Modal>
  );
}
